// Plays the decoded buffer (sample-accurate) or, for very long files, an <audio> element.
type Listener = () => void;

// One AudioContext for the whole page: iOS limits how many a page may create.
let shared: AudioContext | null = null;
function audioContext(): AudioContext {
  if (!shared) {
    // Without this, iOS mutes Web Audio when the ringer switch is on silent (Safari 16.4+).
    const session = (navigator as Navigator & { audioSession?: { type: string } }).audioSession;
    if (session) {
      try { session.type = 'playback'; } catch { /* not supported */ }
    }
    shared = new AudioContext();
  }
  return shared;
}

/**
 * iOS only lets audio start inside a user gesture. On the first tap or key press,
 * create and resume the context and play one silent sample, so that later
 * playback (even after an await) is allowed.
 */
function unlock() {
  const ctx = audioContext();
  void ctx.resume();
  const b = ctx.createBuffer(1, 1, ctx.sampleRate);
  const src = ctx.createBufferSource();
  src.buffer = b;
  src.connect(ctx.destination);
  src.start(0);
  if (ctx.state === 'running') for (const ev of ['touchend', 'pointerup', 'keydown']) window.removeEventListener(ev, unlock, true);
}
for (const ev of ['touchend', 'pointerup', 'keydown']) window.addEventListener(ev, unlock, true);

export class Player {
  private ctx: AudioContext | null = null;
  private src: AudioBufferSourceNode | null = null;
  private gain: GainNode | null = null;
  private el: HTMLAudioElement | null = null;
  private startedAt = 0; // ctx time when playback began
  private offset = 0; // file time at startedAt
  private stopAt: number | null = null;
  private loop: [number, number] | null = null;
  private listeners = new Set<Listener>();
  playing = false;
  /** Current position when paused. */
  private pos = 0;

  constructor(private buffer: AudioBuffer | null, private url: string | null) {}

  on(fn: Listener) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
  private emit() {
    this.listeners.forEach((f) => f());
  }

  get time(): number {
    if (!this.playing) return this.pos;
    if (this.el) return this.el.currentTime;
    if (!this.ctx) return this.pos;
    let t = this.offset + (this.ctx.currentTime - this.startedAt);
    if (this.loop) {
      const [a, b] = this.loop;
      if (t > b) t = a + ((t - a) % Math.max(1e-3, b - a));
    }
    return t;
  }

  seek(t: number) {
    const was = this.playing;
    const loop = this.loop, stop = this.stopAt;
    this.stopInternal();
    this.pos = Math.max(0, t);
    if (was) this.play(this.pos, stop ?? undefined, !!loop);
    else this.emit();
  }

  /** Plays from `from` to `to` (or the end). With `loop`, repeats [from, to]. */
  play(from: number, to?: number, loop = false) {
    this.stopInternal();
    this.pos = from;
    if (this.buffer) {
      this.ctx ??= audioContext();
      if (this.ctx.state !== 'running') void this.ctx.resume();
      const ctx = this.ctx;
      const src = ctx.createBufferSource();
      src.buffer = this.buffer;
      const g = ctx.createGain();
      src.connect(g).connect(ctx.destination);
      const now = ctx.currentTime + 0.01;
      // A 4 ms fade avoids clicks when a frame starts or stops mid-waveform.
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(1, now + 0.004);
      if (loop && to !== undefined) {
        src.loop = true;
        src.loopStart = from;
        src.loopEnd = to;
        src.start(now, from);
        this.loop = [from, to];
      } else {
        const dur = to !== undefined ? Math.max(0.005, to - from) : undefined;
        src.start(now, from, dur);
        if (dur !== undefined) {
          g.gain.setValueAtTime(1, now + Math.max(0.004, dur - 0.004));
          g.gain.linearRampToValueAtTime(0, now + dur);
        }
        this.stopAt = to ?? null;
      }
      src.onended = () => {
        if (this.src !== src) return;
        this.playing = false;
        this.pos = to ?? this.buffer!.duration;
        this.src = null;
        this.emit();
      };
      this.src = src;
      this.gain = g;
      this.startedAt = now;
      this.offset = from;
    } else if (this.url) {
      this.el ??= new Audio(this.url);
      const el = this.el;
      el.currentTime = from;
      el.loop = false;
      void el.play();
      this.stopAt = to ?? null;
      const tick = () => {
        if (!this.playing || this.el !== el) return;
        if (this.stopAt !== null && el.currentTime >= this.stopAt) {
          if (loop && to !== undefined) el.currentTime = from;
          else { this.pause(); this.pos = this.stopAt; this.emit(); return; }
        }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
      el.onended = () => { this.playing = false; this.emit(); };
    } else return;
    this.playing = true;
    this.emit();
  }

  pause() {
    const t = this.time;
    this.stopInternal();
    this.pos = t;
    this.emit();
  }

  private stopInternal() {
    if (this.src) {
      const s = this.src;
      this.src = null;
      try {
        this.gain?.gain.setTargetAtTime(0, this.ctx!.currentTime, 0.003);
        s.stop(this.ctx!.currentTime + 0.015);
      } catch { /* already stopped */ }
    }
    if (this.el) this.el.pause();
    this.playing = false;
    this.loop = null;
    this.stopAt = null;
  }

  dispose() {
    this.stopInternal();
    if (this.url) URL.revokeObjectURL(this.url);
    this.listeners.clear();
  }
}
